import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { rteSettings } from '../../utils';
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import '../../../node_modules/react-quill/dist/quill.snow.css';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import FormCheckBox from '../../components/FormCheckBox';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { RouteParams } from '../../Interfaces';
import { ChildSafeStandardEditCommand } from '../../api/models';

const ContentChildSafeStandardsDetail: React.FC = () => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get all states from Redux store
	const states = useAppSelector((state) => state.lists.states);

	// Get Id from params
	const params = useParams<RouteParams>();

	// Setting up RTE State
	const [textEditor, setTextEditor] = React.useState(' ');

	const textEditorChangeHandler = (e: string) => {
		setTextEditor(e);
	};

	// Set up data state and initial data
	const initialData = {
		id: undefined,
		title: undefined,
		description: undefined,
		pageContent: undefined,
		states: []
	};

	const [childSafetyStandardsDetailData, setChildSafetyStandardsDetailData] =
		React.useState<ChildSafeStandardEditCommand>(initialData);

	const getChildSafetyStandardDetailData = async () => {
		if (params.id) {
			setIsLoading(true);

			const response = await apibridge.getChildSafeStandardEdit(params.id);

			response.result = response.result || initialData;

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				setChildSafetyStandardsDetailData(response.result);

				setTextEditor(decodeURIComponent(response.result.pageContent));
			}
		} else {
			setIsLoading(false);

			setChildSafetyStandardsDetailData(initialData);

			setTextEditor(initialData.pageContent || '');
		}
	};

	React.useEffect(() => {
		getChildSafetyStandardDetailData();
	}, []);

	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const text = encodeURIComponent(textEditor);

		const childSafeData: ChildSafeStandardEditCommand = {
			id: childSafetyStandardsDetailData.id,
			title: values.headingInput,
			description: values.subheadingInput,
			pageContent: text, //textEditor,
			states: values.statesAppliedInput
		};

		const response = await apibridge.postChildSafeStandardEdit(childSafeData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/content-child-safe-standards');
		}
	};

	const validationSchema = Yup.object({
		headingInput: Yup.string().required('Required'),
		subheadingInput: Yup.string().required('Required'),
		statesAppliedInput: Yup.array().required('Required')
	});

	const initialValues = {
		headingInput: childSafetyStandardsDetailData.title,
		subheadingInput: childSafetyStandardsDetailData.description,
		statesAppliedInput: childSafetyStandardsDetailData.states
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<SwitchTransition mode="out-in">
								<CSSTransition
									key={`key_${isLoading}`}
									timeout={600}
									classNames="standalone-fade"
									mountOnEnter
									unmountOnExit
								>
									{isLoading ? (
										<LoadingSpinner show={isLoading} />
									) : (
										<>
											<div className="general-form-layout-wrapper direction-normal-column">
												<div className="form-main">
													<div className="form-group">
														<label htmlFor="headingInput" className="label">
															Safety standard title
														</label>

														<div className="form-field">
															<Field name="headingInput" id="headingInput" type="text" className="form-input" />

															<ErrorMessage name="headingInput" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group">
														<label htmlFor="subheadingInput" className="label">
															Short description
														</label>

														<div className="form-field">
															<Field
																as="textarea"
																name="subheadingInput"
																id="subheadingInput"
																type="text"
																className="form-input"
															/>

															<ErrorMessage name="subheadingInput" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group">
														<label htmlFor="contentInput" className="label">
															Detailed page content
														</label>

														<div className="form-field">
															<ReactQuill
																modules={rteSettings.modules}
																formats={rteSettings.formats}
																value={textEditor}
																onChange={textEditorChangeHandler}
															/>
														</div>
													</div>
												</div>

												<div className="general-form-guide content">
													<div className="form-group">
														<label htmlFor="statesAppliedInput" className="label">
															Applicable States and Territories
														</label>

														<div className="form-field">
															<FormCheckBox
																values={childSafetyStandardsDetailData.states || []}
																options={states}
																onChange={(vals: string[]) => formik.setFieldValue('statesAppliedInput', vals)}
															/>
														</div>
													</div>
												</div>
											</div>

											<div className="general-divider"></div>

											<div className="form-button-group">
												<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
													Save
												</Button>
											</div>
										</>
									)}
								</CSSTransition>
							</SwitchTransition>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default ContentChildSafeStandardsDetail;
